// src/components/TCOFlotas.jsx
import React, { useMemo, useState, useRef } from "react";
import { calcTCURenting } from "../engine/tcoFleetEngine";
import KiaSwal from '../utils/swal';
import html2canvas from 'html2canvas';
import jsPDF from 'jspdf';
import MouseLightEffect from './MouseLightEffect';
import { useTrack } from '../analytics/TrackingContext';
import { useFeatureFlags } from '../features/FeatureFlagsContext';

const newVehicle = (name = "Vehículo") => ({
  name,
  // Renting
  consumoRenting: "",
  cuotaSinIVA: "",
  precioLitro: "",
});

const num = (v) => (Number.isFinite(Number(v)) ? Number(v) : 0);

export default function TCOFlotas() {
  const { trackEvent } = useTrack();
  const { isEnabled } = useFeatureFlags();
  const [duracionMeses, setDuracionMeses] = useState("");
  const [kmAnuales, setKmAnuales] = useState("");

  const [vehiculos, setVehiculos] = useState([]);

  const [showAddModal, setShowAddModal] = useState(false);
  const [newVehicleName, setNewVehicleName] = useState("");
  const [globalErrorMessage, setGlobalErrorMessage] = useState("");

  const [showAhorro, setShowAhorro] = useState(false);
  const [diferenciaMensual, setDiferenciaMensual] = useState(0);
  const [diferenciaAnual, setDiferenciaAnual] = useState(0);
  const [numCochesAhorro, setNumCochesAhorro] = useState(1);

  const tcoRef = useRef(null);

  const kmPeriodo = useMemo(
    () => num(duracionMeses) * (num(kmAnuales) / 12),
    [duracionMeses, kmAnuales]
  );

  const resultados = useMemo(() => {
    const dur = Math.max(1, num(duracionMeses));
    const kmA = Math.max(0, num(kmAnuales));

    return vehiculos.map((v) =>
      calcTCURenting({
        duracionMeses: dur,
        kmAnuales: kmA,
        precioLitro: num(v.precioLitro),
        vehiculo: {
          consumoL100: num(v.consumoRenting),
          cuotaSinIVA: num(v.cuotaSinIVA),
        },
      })
    );
  }, [vehiculos, duracionMeses, kmAnuales]);

  const updateVehicle = (idx, patch) => {
    setVehiculos((prev) => prev.map((v, i) => (i === idx ? { ...v, ...patch } : v)));
  };

  const addVehicle = () => {
    if (vehiculos.length >= 3) {
      setGlobalErrorMessage("No se pueden añadir más de 3 vehículos a la comparativa.");
      setTimeout(() => setGlobalErrorMessage(""), 3000);
    } else {
      setShowAddModal(true);
    }
  };
  const removeVehicle = (idx) => setVehiculos((p) => p.filter((_, i) => i !== idx));

  const confirmAddVehicle = () => {
    if (newVehicleName.trim()) {
      setVehiculos((p) => [...p, newVehicle(newVehicleName.trim())]);
      setNewVehicleName("");
      setShowAddModal(false);
    }
  };

  const cancelAddVehicle = () => {
    setNewVehicleName("");
    setShowAddModal(false);
  };

  const calcularAhorro = () => {
    if (resultados.length >= 2) {
      const tcus = resultados.map(r => r.tcuMes);
      const maxTcu = Math.max(...tcus);
      const minTcu = Math.min(...tcus);
      const diffMes = maxTcu - minTcu;
      setDiferenciaMensual(diffMes);
      setDiferenciaAnual(diffMes * 12);
      setShowAhorro(true);
      trackEvent('calculation', 'tco_flotas');
    }
  };

  const resetAll = () => {
    setDuracionMeses("");
    setKmAnuales("");
    setVehiculos([]);
    setShowAhorro(false);
    setDiferenciaMensual(0);
    setDiferenciaAnual(0);
    setNumCochesAhorro(1);
  };

  const añadirPaginaLegal = (pdf) => {
    pdf.addPage();
    const margin = 14;
    const pageWidth = pdf.internal.pageSize.getWidth();
    const maxWidth = pageWidth - margin * 2;
    let y = 16;

    const block = (text, fontSize, bold, indent = 0) => {
      pdf.setFontSize(fontSize);
      pdf.setFont('helvetica', bold ? 'bold' : 'normal');
      const lines = pdf.splitTextToSize(text, maxWidth - indent);
      lines.forEach(line => {
        if (y > 280) { pdf.addPage(); y = 16; }
        pdf.text(line, margin + indent, y);
        y += fontSize * 0.45;
      });
      y += 1.5;
    };

    block('AVISO LEGAL — CONDICIONES DE USO DEL SIMULADOR', 11, true);
    y += 2;
    block('1. NATURALEZA Y FINALIDAD DE LA SIMULACIÓN', 9, true);
    block('1.1. El presente simulador tiene carácter meramente informativo y orientativo. Su finalidad es ofrecer al usuario (en adelante, el "Usuario") una estimación comparativa del precio y los costes de uso de un vehículo de la Gama 0 Emisiones Kia (vehículo eléctrico –VE– o híbrido enchufable–PHEV–) frente a otro u otros vehículos seleccionados.', 8, false);
    block('1.2. Los resultados obtenidos no constituyen oferta comercial vinculante, presupuesto ni compromiso contractual alguno por parte del Concesionario. Las condiciones definitivas de adquisición se determinarán, en su caso, en el correspondiente contrato de compraventa.', 8, false);
    y += 2;
    block('2. ORIGEN Y TRATAMIENTO DE LOS DATOS', 9, true);
    block('2.1. Los datos utilizados en la simulación proceden de las siguientes fuentes:', 8, false);
    block('a) Datos facilitados por el Usuario al comercial del Concesionario: precio del combustible (diésel o gasolina), kilómetros diarios recorridos de lunes a domingo, posibilidad de realizar cargas completas de batería durante un día laborable y prestaciones o equipamiento deseados.', 8, false, 4);
    block('b) Datos del vehículo: precio (IVA incluido), impuesto de matriculación y demás impuestos, en su caso, a determinar, acabado, motorización y consumo medio homologado conforme al ciclo WLTP, obtenidos de la página web oficial de la marca o de la documentación técnica oficial del vehículo seleccionado por el Usuario.', 8, false, 4);
    block('c) Precio de la energía eléctrica: obtenido de la página web de la empresa distribuidora de electricidad indicada por el Usuario o, en su defecto, del precio medio de la electricidad a nivel nacional publicado por fuentes oficiales.', 8, false, 4);
    block('2.2. El Concesionario ha actuado con la diligencia debida en la obtención e incorporación de los datos al simulador. No obstante, no puede garantizar la exactitud absoluta de los datos proporcionados por el Usuario ni de aquellos extraídos de fuentes de terceros, al depender de información actualizada por dichos terceros en sus respectivas páginas web o documentación.', 8, false);
    block('2.3. El Concesionario no mantiene vinculación alguna con los terceros cuyos datos o páginas web se utilicen como fuente para la simulación.', 8, false);
    y += 2;
    block('3. ESTIMACIÓN DE COSTES DE ENERGÍA', 9, true);
    block('3.1. La estimación de coste anual en electricidad o combustible (expresada en euros) se calcula sobre la base del coste diario de energía, conforme a los siguientes parámetros facilitados por el Usuario:', 8, false);
    block('a) Kilómetros diarios recorridos de lunes a domingo.', 8, false, 4);
    block('b) Precio del combustible (diésel o gasolina) indicado por el Usuario.', 8, false, 4);
    block('c) Posibilidad de realizar cargas completas de batería eléctrica durante un día laborable, entendiéndose por carga completa la necesaria para alcanzar el 100 % de la autonomía eléctrica según la homologación WLTP del vehículo.', 8, false, 4);
    block('3.2. El consumo medio homologado del vehículo comparado corresponde al dato oficial publicado en la página web de la marca o en la documentación técnica oficial del vehículo, conforme al procedimiento de ensayo WLTP. El consumo real puede variar en función de las condiciones de conducción, climatología, estilo de conducción y otros factores.', 8, false);
    y += 2;
    block('4. LIMITACIÓN DE RESPONSABILIDAD', 9, true);
    block('4.1. El Concesionario no será responsable de las inexactitudes que puedan derivarse de:', 8, false);
    block('a) Datos incorrectos, incompletos o desactualizados facilitados por el Usuario.', 8, false, 4);
    block('b) Variaciones en los precios de combustible, electricidad o del vehículo posteriores a la fecha de realización de la simulación.', 8, false, 4);
    block('c) Errores u omisiones contenidos en las páginas web oficiales o documentación de terceros consultadas a la fecha indicada en la simulación.', 8, false, 4);
    block('4.2. La presente limitación de responsabilidad no afectará en ningún caso a los derechos que la legislación vigente en materia de protección de consumidores y usuarios reconoce al Usuario con carácter irrenunciable.', 8, false);
  };

  const generarPDF = () => {
    const header = document.querySelector('header');
    const asesorHeaderText = document.querySelector('.asesor-header-text');
    // const logo = document.querySelector('.LogoH');
    const aviso = document.querySelector('.section-container-aviso');

    if (!tcoRef.current) {
        console.error("Error: No se encontró el contenido del TCO.");
        KiaSwal.fire({ icon: 'error', title: 'Error', text: 'No se puede generar el PDF en este momento.' });
        return;
    }

    // Crear un contenedor temporal para el PDF
    const tempDiv = document.createElement('div');
    tempDiv.style.background = '#fff';
    tempDiv.style.padding = '24px';
    tempDiv.style.width = tcoRef.current.offsetWidth + 'px';
    tempDiv.style.boxSizing = 'border-box';
    tempDiv.style.width = '1000px'; // Aumentar el ancho base
    tempDiv.style.fontSize = '16px'; // Aumentar el tamaño base de la fuente

    // Clonar y añadir elementos
    // if (logo) {
    //     const logoClone = logo.cloneNode(true);
    //     logoClone.style.marginBottom = '16px';
    //     tempDiv.appendChild(logoClone);
    // }
    if (header) {
        const headerClone = header.cloneNode(true);
        headerClone.style.marginBottom = '16px';
        tempDiv.appendChild(headerClone);
    }

    if (asesorHeaderText && asesorHeaderText.textContent.trim()) {
      const asesorInfo = document.createElement('div');
      asesorInfo.textContent = `Comercial / Concesionario: ${asesorHeaderText.textContent.replace(/\s+/g, ' ').trim()}`;
      asesorInfo.style.marginBottom = '16px';
      asesorInfo.style.textAlign = 'center';
      asesorInfo.style.fontSize = '16px';
      asesorInfo.style.fontWeight = 'bold';
      asesorInfo.style.color = '#000000';
      tempDiv.appendChild(asesorInfo);
    }

    const tcoClone = tcoRef.current.cloneNode(true);
    tempDiv.appendChild(tcoClone);

    if (aviso) {
        const avisoClone = aviso.cloneNode(true);
        avisoClone.style.marginTop = '24px';
        tempDiv.appendChild(avisoClone);
    }

    // Ocultar botones y ajustar estilos
    const botones = tempDiv.querySelectorAll('button');
    botones.forEach(boton => boton.style.display = 'none');

    const elementosTexto = tempDiv.querySelectorAll('*');
    elementosTexto.forEach(elemento => {
        elemento.style.setProperty('color', '#000000', 'important');
        // Forzar color negro específicamente para los resultados
        if (elemento.closest('.res')) {
            elemento.style.setProperty('color', '#000000', 'important');
            elemento.style.setProperty('opacity', '1', 'important');
        }
        if (elemento.tagName.toLowerCase() === 'strong') {
            elemento.style.setProperty('font-weight', 'bold', 'important');
            elemento.style.setProperty('color', '#000000', 'important');
        }

        // Ajustar tamaños según el tipo de elemento
        if (elemento.tagName === 'H3') {
            elemento.style.setProperty('font-size', '24px', 'important');
        }
        if (elemento.closest('.res')) {
            elemento.style.setProperty('color', '#000000', 'important');
            elemento.style.setProperty('opacity', '1', 'important');
            elemento.style.setProperty('font-size', '18px', 'important');
        }
        if (elemento.tagName.toLowerCase() === 'strong') {
            elemento.style.setProperty('font-weight', 'bold', 'important');
            elemento.style.setProperty('color', '#000000', 'important');
            elemento.style.setProperty('font-size', '20px', 'important');
        }
        if (elemento.tagName === 'P') {
            elemento.style.setProperty('font-size', '16px', 'important');
        }
        if (elemento.tagName === 'LABEL') {
            elemento.style.setProperty('font-size', '16px', 'important');
        }
    });

        // Ajustar estilos de los contenedores de input
    const inputContainers = tempDiv.querySelectorAll('.input');
    inputContainers.forEach(container => {
        container.style.display = 'flex';
        container.style.alignItems = 'center';
        container.style.justifyContent = 'space-between';
        container.style.marginBottom = '15px';
        container.style.padding = '8px 0';
    });

    // Ajustar estilos de las etiquetas
    const labels = tempDiv.querySelectorAll('.input label');
    labels.forEach(label => {
        label.style.flex = '0 0 60%';
        label.style.marginRight = '20px';
        label.style.textAlign = 'right';
        label.style.fontSize = '16px';
        label.style.fontWeight = 'normal';
    });

    // Ajustar valores de input (convertidos a span)
    const inputs = tempDiv.querySelectorAll('input');
    inputs.forEach(input => {
        const valor = input.value;
        const span = document.createElement('span');
        span.textContent = valor;
        span.style.flex = '0 0 30%';
        span.style.textAlign = 'left';
        span.style.color = '#000000';
        span.style.fontWeight = 'bold';
        span.style.fontSize = '18px';
        span.style.minWidth = '100px';
        input.parentNode.replaceChild(span, input);
    });

    // Ajustar alineación de las columnas
    const columnas = tempDiv.querySelectorAll('.columna');
    columnas.forEach(columna => {
        columna.style.padding = '20px';
        columna.style.display = 'flex';
        columna.style.flexDirection = 'column';
        columna.style.alignItems = 'stretch';
    });

    // Asegurarse de que los párrafos dentro de .res sean negros
    const resultados = tempDiv.querySelectorAll('.res p');
    resultados.forEach(resultado => {
        resultado.style.setProperty('color', '#000000', 'important');
        resultado.style.setProperty('opacity', '1', 'important');
    });

    tempDiv.style.position = 'fixed';
    tempDiv.style.left = '-9999px';
    document.body.appendChild(tempDiv);

    // Configuración optimizada de html2canvas
    const canvasOptions = {
        scale: 2.5, // Reducido de 2 a 1.5 para menor tamaño
        backgroundColor: '#ffffff',
        useCORS: true,
        logging: false,
        windowWidth: tempDiv.offsetWidth,
        windowHeight: tempDiv.offsetHeight,
        imageTimeout: 0,
        removeContainer: true
    };

    html2canvas(tempDiv, canvasOptions)
    .then((canvas) => {
        // Convertir a JPEG con compresión optimizada
        const imgData = canvas.toDataURL('image/jpeg', 0.85);
        
        const pdf = new jsPDF({
            orientation: 'portrait',
            unit: 'mm',
            format: 'a4',
            compress: true // Activar compresión del PDF
        });

        const pageWidth = pdf.internal.pageSize.getWidth();
        const pageHeight = pdf.internal.pageSize.getHeight();
        const imgWidth = pageWidth - 20; // Margen de 10mm en cada lado
        const imgHeight = (canvas.height * imgWidth) / canvas.width;

        // Si la altura es mayor que la página, ajustamos por altura
        if (imgHeight > pageHeight - 20) {
            const ratio = (pageHeight - 20) / imgHeight;
            pdf.addImage(
                imgData, 
                'JPEG', 
                10, 
                10, 
                imgWidth * ratio, 
                pageHeight - 20,
                null,
                'FAST'
            );
        } else {
            const y = (pageHeight - imgHeight) / 2;
            pdf.addImage(
                imgData, 
                'JPEG', 
                10, 
                y, 
                imgWidth, 
                imgHeight,
                null,
                'FAST'
            );
        }

        // --- Página 2: Nota legal ---
        añadirPaginaLegal(pdf);

        pdf.save('Simulacion_TCO.pdf');
        trackEvent('pdf_download', 'tco_flotas', { filename: 'Simulacion_TCO.pdf' });
        KiaSwal.fire({ icon: 'success', title: 'PDF generado', text: 'El archivo se ha descargado correctamente.' });
    })
    .catch((error) => {
        console.error("Error al generar el PDF:", error);
        KiaSwal.fire({ icon: 'error', title: 'Error', text: 'Hubo un error al generar el PDF.' });
    })
    .finally(() => {
        document.body.removeChild(tempDiv);
    });
  };

  return (
    <div ref={tcoRef} className="app-container" style={{ display: 'flex', flexDirection: 'column' }}>
      {/* Bloque parámetros globales */}
      <MouseLightEffect>
        <div className="section-container" style={{ maxWidth: 950, width: '100%' }}>
          <h2>Renting (TCU)</h2>

        <div className="input">
          <label>Duración (meses)</label>
          <input
            type="number"
            value={duracionMeses}
            onChange={(e) => setDuracionMeses(e.target.value)}
            min={1}
          />
        </div>

        <div className="input">
          <label>Kilómetros anuales</label>
          <input
            type="number"
            value={kmAnuales}
            onChange={(e) => setKmAnuales(e.target.value)}
            min={0}
          />
        </div>

        <div className="res">
          <p>
            <strong>Km periodo:</strong> {Number.isFinite(kmPeriodo) ? kmPeriodo.toFixed(0) : "—"} km
          </p>
        </div>

        <div className="button" style={{ gap: 12, flexWrap: "wrap" }}>
          <button className="calc" onClick={addVehicle}>
            + Añadir Vehículo
          </button>
          <button className="reset-button" onClick={resetAll}>
            Borrar Datos
          </button>
        </div>
      </div>
      </MouseLightEffect>

      {globalErrorMessage && <p style={{ color: 'red', marginTop: 10, textAlign: 'center' }}>{globalErrorMessage}</p>}

      {/* Comparativa vehículos (mismo look que vuestra comparativa) */}
      <MouseLightEffect>
        <div className="section-container section-container-comparativa" style={{ maxWidth: 950, width: '100%' }}>
          <h2>Comparativa</h2>

        <div className="comparativa-grid">
          {vehiculos.map((v, idx) => {
            const r = resultados[idx] || {};
            const kpi = r.tcuMes;

            return (
              <div className="columna" key={idx}>
                <h3 style={{ marginTop: 0 }}>
                  <input
                    style={{
                      width: "100%",
                      border: "none",
                      background: "transparent",
                      textAlign: "center",
                      fontFamily: "Kiafontn",
                      fontSize: "1.05rem",
                      outline: "none",
                    }}
                    value={v.name}
                    onChange={(e) => updateVehicle(idx, { name: e.target.value })}
                  />
                </h3>

                <div className="input">
                  <label>Consumo Vehiculo (kWh/100km - L/100Km)</label>
                  <input
                    type="number"
                    step="0.1"
                    value={v.consumoRenting}
                    onChange={(e) => updateVehicle(idx, { consumoRenting: e.target.value })}
                  />
                </div>
                <div className="input">
                  <label>Cuota sin IVA (€/mes)</label>
                  <input
                    type="number"
                    value={v.cuotaSinIVA}
                    onChange={(e) => updateVehicle(idx, { cuotaSinIVA: e.target.value })}
                  />
                </div>
                <div className="input">
                  <label>Precio Combustible (€/L ó €/Kwh)</label>
                  <input
                    type="number"
                    step="0.01"
                    value={v.precioLitro}
                    onChange={(e) => updateVehicle(idx, { precioLitro: e.target.value })}
                    min={0}
                  />
                </div>

                <div className="res resultado-animado">
                  <p>IVA: {Number.isFinite(r.ivaMes) ? r.ivaMes.toFixed(2) : "—"} €</p>
                  <p>
                    Cuota con IVA: {Number.isFinite(r.cuotaConIVA) ? r.cuotaConIVA.toFixed(2) : "—"} €
                  </p>
                  <p>
                    Combustible: {Number.isFinite(r.combustibleMes) ? r.combustibleMes.toFixed(2) : "—"} €
                  </p>
                  <div style={{ background: '#2e2e3d5c', backdropFilter: 'blur(10px)', border: '1px solid #ccc', borderRadius: '5px', padding: '10px', boxShadow: '0 4px 6px rgba(0, 0, 0, 0.1)', marginTop: '10px' }}>
                    <p style={{ fontWeight: 'bold', fontSize: '1.1em', margin: 0 }}>
                      <strong>TCU €/mes:</strong> {Number.isFinite(kpi) ? kpi.toFixed(2) : "—"}
                    </p>
                  </div>
                  <p>
                    Coste Km/mes: {Number.isFinite(kpi) && num(kmAnuales) > 0 ? (kpi / (num(kmAnuales) / 12)).toFixed(2) : "—"} €/km
                  </p>
                </div>

                {vehiculos.length > 1 && (
                  <div className="button">
                    <button className="reset-button" onClick={() => removeVehicle(idx)}>
                      Eliminar
                    </button>
                  </div>
                )}
              </div>
            );
          })}
        </div>

        <div className="button" style={{ gap: 12, flexWrap: "wrap", marginTop: 20 }}>
          <button className="calc" onClick={calcularAhorro} disabled={vehiculos.length < 2}>
            Calcular Ahorro
          </button>
        </div>
      </div>
      </MouseLightEffect>

      {showAhorro && (
        <MouseLightEffect>
          <div className="section-container" style={{ maxWidth: 950, marginTop: 20, width: '100%' }}>
            <h3>Ahorro Flotas</h3>
          <p>Ahorro mensual: {diferenciaMensual.toFixed(2)} €</p>
          <p>Ahorro anual: {diferenciaAnual.toFixed(2)} €</p>
          <div className="input">
            <label>Unidades Flota</label>
            <input
              type="number"
              value={numCochesAhorro}
              onChange={(e) => setNumCochesAhorro(e.target.value)}
              min={1}
            />
          </div>
          <p>Ahorro anual total de la flota: {(diferenciaAnual * num(numCochesAhorro)).toFixed(2)} €</p>
          {isEnabled('pdf_tco') && <button className="calc" onClick={generarPDF}>Descargar Simulación TCU</button>}
        </div>
        </MouseLightEffect>
      )}

      {showAddModal && (
        <div
          style={{
            position: "fixed",
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: "rgba(0, 0, 0, 0.5)",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            zIndex: 1000,
          }}
          onClick={cancelAddVehicle}
        >
          <div
            className="section-container"
            style={{
              maxWidth: 400,
              margin: 0,
              cursor: "default",
            }}
            onClick={(e) => e.stopPropagation()}
          >
            <h3>Añadir Vehículo</h3>
            <div className="input">
              <label>Nombre del Vehículo</label>
              <input
                type="text"
                value={newVehicleName}
                onChange={(e) => setNewVehicleName(e.target.value)}
                placeholder="Ingrese el nombre"
                onKeyDown={(e) => {
                  if (e.key === "Enter") confirmAddVehicle();
                  if (e.key === "Escape") cancelAddVehicle();
                }}
              />
            </div>
            <div className="button" style={{ gap: 12, flexWrap: "wrap" }}>
              <button className="calc" onClick={confirmAddVehicle}>
                Añadir
              </button>
              <button className="reset-button" onClick={cancelAddVehicle}>
                Cancelar
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
    
  );
}
